import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getImage } from '../data/images';
import { PROPERTY_PRICE_DISPLAY, WHATSAPP_URL } from '../data/contact';
import { trackEvent } from '../utils/analytics';
import { BrokerageLine } from './BrokerageLine';
export function HeroSection() {
  const {
    lang,
    t
  } = useLanguage();
  const exterior = getImage('exterior');
  const inset = getImage('openKitchenLiving');
  return <section id="top" className="relative isolate w-full overflow-hidden bg-midnight" data-id="element-279" data-mp="30ksmjtm7o9wg">
      {exterior && <img src={exterior.src} alt={lang === 'en' ? exterior.altEn : exterior.altEs} width={exterior.width} height={exterior.height} fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{
      objectPosition: exterior.objectPosition
    }} data-id="element-280" data-mp="2mrtc461ad5vr" />}
      <div aria-hidden="true" className="absolute inset-0 bg-midnight/45 lg:bg-[linear-gradient(90deg,rgba(6,24,39,0.88)_0%,rgba(6,24,39,0.72)_42%,rgba(6,24,39,0.25)_72%,rgba(6,24,39,0.15)_100%)]" data-id="element-281" data-mp="2fgbj2voirz8p" />

      <div className="relative mx-auto flex min-h-[100svh] max-w-editorial flex-col justify-end px-5 pb-24 pt-32 lg:grid lg:min-h-[92vh] lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-28 lg:pt-36" data-id="element-282" data-mp="2p7elq1x3kma3">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:col-span-7" data-id="element-283" data-mp="2z5c9ypeyij2p">
          <p className="label-caps text-gold" data-id="element-284" data-mp="3353515binld1">
            {t('Davenport, Florida • Near Disney', 'Davenport, Florida • Cerca de Disney')}
          </p>
          <div className="gold-rule mt-4 w-16" data-id="element-285" data-mp="1dqom51n6ispg" />

          <h1 className="mt-6 max-w-[16ch] font-display text-[42px] font-semibold uppercase leading-[0.95] text-white sm:text-[56px] lg:text-[72px]" data-id="element-286" data-mp="18rbqpsi8sdb9">
            {t('Fully Furnished Water-View Townhome', 'Townhome Totalmente Amoblada con Vista al Agua')}
          </h1>

          <p className="mt-7 font-display text-[52px] font-medium leading-none text-gold sm:text-[64px] lg:text-[76px]" data-id="element-287" data-mp="3duiivsq6t9vp">
            {PROPERTY_PRICE_DISPLAY}
          </p>

          <p className="label-caps mt-4 text-white/85" data-id="element-288" data-mp="3munopyfqg07r">
            {t('3 Bedrooms • 2.5 Bathrooms • 1,622 Sq Ft', '3 Habitaciones • 2.5 Baños • 1,622 Pies²')}
          </p>

          <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-white/80 lg:text-base" data-id="element-289" data-mp="l49u1d17wmu">
            {t('Move-in-ready comfort, beautiful water views and resort-style amenities near Central Florida’s world-famous attractions.', 'Comodidad lista para disfrutar, hermosas vistas al agua y amenidades estilo resort cerca de las atracciones más reconocidas de Florida Central.')}
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-290" data-mp="jcnc25z8f6nt">
            <a href="#contact" onClick={() => trackEvent('schedule_tour_click', {
            location: 'hero'
          })} className="bg-gold px-7 py-4 text-center font-display text-[13px] font-medium uppercase tracking-[0.16em] text-midnight transition-colors duration-150 ease-out hover:bg-gold-soft" data-id="element-291" data-mp="23vzj53muddon">
              {t('Schedule a Private Tour', 'Agenda una Visita Privada')}
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('whatsapp_click', {
            location: 'hero'
          })} className="inline-flex items-center justify-center gap-2 border border-white/45 px-7 py-4 font-display text-[13px] font-medium uppercase tracking-[0.16em] text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold" data-id="element-292" data-mp="2mmqo7szsqhx8">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-293" data-mp="31glselp0bj23" />
              {t('Chat on WhatsApp', 'Escríbele por WhatsApp')}
            </a>
          </div>

          <BrokerageLine tone="light" className="mt-4" data-id="element-294" data-mp="2q20nedgwixv1" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-12 lg:col-span-5 lg:mt-0 lg:justify-self-end" data-id="element-295" data-mp="2c291lqk58d6h">
          {inset && <figure className="arch-frame relative mx-auto h-[320px] w-full max-w-[300px] border border-white/15 sm:h-[420px] sm:max-w-[360px] lg:h-[520px] lg:max-w-[380px]" data-id="element-296" data-mp="34t0x0ltdwxsr">
              <img src={inset.src} alt={lang === 'en' ? inset.altEn : inset.altEs} width={inset.width} height={inset.height} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
            objectPosition: inset.objectPosition
          }} data-id="element-297" data-mp="2v0ld3iyj1z58" />
            </figure>}
        </motion.div>
      </div>

      <motion.a href="#property" aria-label={t('Scroll to property details', 'Desplázate a los detalles de la propiedad')} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-white/70 lg:block" animate={{
      y: [0, 8, 0]
    }} transition={{
      duration: 1.8,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-298" data-mp="1ol4qxrbg41y">
        <ChevronDownIcon className="h-7 w-7" aria-hidden="true" data-id="element-299" data-mp="3iwgjr5c5olqn" />
      </motion.a>
    </section>;
}